import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { StreamingTrackRow } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { DeltaPill } from '../../components/primitives/DeltaPill';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { formatChangePct, formatStreamingNumber } from '../streamingFormat';
import { sortTracks } from '../sortTracks';
import { useTrackSort } from '../hooks/useTrackSort';
import { streamingTotalLabels } from '../streamingTotalsMessages';
import { streamingBlockMessages as messages } from '../streamingBlockMessages';
import { streamingEarningsMessages } from '../streamingEarningsMessages';
import { SortHeader } from './SortHeader';
import { MoneyList } from './MoneyList';
import { TrackName } from './TrackName';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TracksTableProps {
  tracks: StreamingTrackRow[];
  /** True: the Net earnings column has no amounts (a member, design e2; or the plan). */
  earningsHidden: boolean;
  /**
   * True when the plan hides the earnings (Free, `hiddenReason: 'plan'`): the column stays, with a
   * lock that opens the upgrade prompt and no amount. Else a hidden column is not drawn.
   */
  earningsLockedByPlan?: boolean;
  onOpen: (track: StreamingTrackRow) => void;
}

/** The track list (design d1): rank, track, streams, listeners, saves, net earnings and change. */
const TracksTableComponent: React.FC<TracksTableProps> = ({
  tracks,
  earningsHidden,
  earningsLockedByPlan = false,
  onOpen,
}) => {
  const intl = useIntl();
  const { sort, sortBy } = useTrackSort();
  const openUpgradePrompt = useUpgradePrompt();
  const openEarningsPrompt = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.earnings), [openUpgradePrompt]);
  const showLockedColumn = earningsHidden && earningsLockedByPlan;
  const rows = useMemo(() => sortTracks(tracks, sort.key, sort.descending), [sort, tracks]);

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table} data-testid="streaming-tracks-table">
        <thead>
          <tr>
            <th>{intl.formatMessage(messages.colRank)}</th>
            <SortHeader
              column="name"
              label={intl.formatMessage(messages.colTrack)}
              isNumber={false}
              sort={sort}
              onSort={sortBy}
            />
            <SortHeader
              column="streams"
              label={intl.formatMessage(streamingTotalLabels.streams)}
              sort={sort}
              onSort={sortBy}
            />
            <SortHeader
              column="listeners"
              label={intl.formatMessage(streamingTotalLabels.listeners)}
              sort={sort}
              onSort={sortBy}
            />
            <SortHeader
              column="saves"
              label={intl.formatMessage(streamingTotalLabels.saves)}
              sort={sort}
              onSort={sortBy}
            />
            {earningsHidden ? null : (
              <SortHeader
                column="earnings"
                label={intl.formatMessage(streamingEarningsMessages.colNetEarnings)}
                sort={sort}
                onSort={sortBy}
              />
            )}
            {showLockedColumn ? (
              <th className={styles.numCell}>
                <button
                  type="button"
                  className={styles.rowLink}
                  onClick={openEarningsPrompt}
                  title={intl.formatMessage(streamingEarningsMessages.planLockedColumn)}
                  data-testid="streaming-tracks-earnings-lock"
                >
                  <FontAwesomeIcon icon={faLock} /> {intl.formatMessage(streamingEarningsMessages.colNetEarnings)}
                </button>
              </th>
            ) : null}
            <SortHeader column="change" label={intl.formatMessage(messages.colChange)} sort={sort} onSort={sortBy} />
          </tr>
        </thead>
        <tbody>
          {rows.map((track, index) => {
            const change = formatChangePct(intl, track.changePct);
            return (
              <tr key={track.key}>
                <td className={styles.rank}>{index + 1}</td>
                <td>
                  <button
                    type="button"
                    className={`${styles.rowLink} ${styles.rankName}`}
                    onClick={() => onOpen(track)}
                  >
                    <TrackName name={track.name} imageUrl={track.imageUrl} />
                  </button>
                </td>
                <td className={styles.numCell}>{formatStreamingNumber(intl, track.streams)}</td>
                <td className={styles.numCell}>{formatStreamingNumber(intl, track.listeners)}</td>
                <td className={styles.numCell}>{formatStreamingNumber(intl, track.saves)}</td>
                {earningsHidden ? null : (
                  <td className={styles.numCell}>
                    <MoneyList amounts={track.earnings} />
                  </td>
                )}
                {showLockedColumn ? <td className={styles.numCell}>—</td> : null}
                <td className={styles.numCell}>
                  {change !== null && track.changePct !== null ? (
                    <DeltaPill value={track.changePct} label={change} />
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const TracksTable = TypedMemo(TracksTableComponent);
